import { Navigate, RouteObject } from "react-router-dom";
import { CircularProgress, Stack, Typography as T } from "@mui/material";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import { ConfiguratorProvider } from "datasets/pages/settings/contexts/configurator";
import ConfigOverview from "datasets/pages/settings/pages/ConfigOverview";
import DomainConfigForm from "datasets/pages/settings/pages/DomainConfigForm";
import yn from "yn";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import { ROUTE_404 } from "~/router/constants";

const { VITE_CE_CONFIGURATOR_ENABLED } = import.meta.env;

export const routeDefinitions: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="domains" replace />,
  },
  {
    path: "domains",
    element: <ConfigOverview />,
  },
  {
    path: "domains/:domainId",
    element: <DomainConfigForm />,
  },
];

/**
 * Empty state shown when the user has permission to configure Cohort Explorer
 * but the dataset has no tables to create domains from.
 */
const ConfiguratorEmptyState = () => {
  return (
    <Stack>
      <T component="h1" fontSize={20} fontWeight={600} lineHeight={1.8}>
        Cohort Explorer Configurator
      </T>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 8,
          px: 4,
          textAlign: "center",
        }}
      >
        <T
          sx={{
            fontSize: 18,
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          Add tables to this dataset to configure the Cohort Explorer.
        </T>
      </Stack>
    </Stack>
  );
};

const Configurator = () => {
  const routes = useSafeRoutes(routeDefinitions);
  const { data: tableFQNs, isLoading } = useDatasetTableFQNs();

  if (!yn(VITE_CE_CONFIGURATOR_ENABLED)) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={12}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  const hasConfiguredTables = !!tableFQNs?.length;
  if (!hasConfiguredTables) {
    return <ConfiguratorEmptyState />;
  }

  return <ConfiguratorProvider>{routes}</ConfiguratorProvider>;
};

export default Configurator;
